/**
 * Fax Agent Service
 * 
 * This service layer provides an abstraction over fax-related API calls.
 * It handles all fax CRUD operations, filtering, and status management.
 * 
 * @module FaxAgentService
 */

import { 
  getFaxes, 
  createFax, 
  updateFax, 
  deleteFax as apiDeleteFax, 
  sendFax as apiSendFax, 
  createFaxTemplate, 
  getFaxTemplates,
  getFaxDashboard,   // Only raw API call - no business logic
  getFaxById,        // Raw API call for fax details by ID
  sendFaxWithFiles,   // Raw API call for sending fax with files
  getPurchasedFaxNumbers,
  sendFaxWithUpload,
  getFaxTemplateStructure,
  getFaxTemplateSignedUrl,
  downloadTemplateFile as apiDownloadTemplateFile,
  downloadFaxFile as apiDownloadFaxFile,
} from '../utils/apiClient';
import * as yup from 'yup';

/**
 * Fetches faxes based on provided API parameters
 * 
 * @param {Object} apiParams - API parameters for filtering
 * @param {boolean} [apiParams.include_inactive] - Whether to include inactive faxes
 * @param {string} [apiParams.direction] - Direction filter (INBOUND, OUTBOUND, DRAFT)
 * @param {string} [apiParams.patient_name] - Patient name filter
 * @param {string} [apiParams.from_date] - Start date filter
 * @param {string} [apiParams.to_date] - End date filter
 * @returns {Promise<Array>} Array of fax objects
 * 
 * @example
 * const faxes = await FaxAgentService.getAll({ direction: 'OUTBOUND', include_inactive: false });
 */
export const getAll = async (apiParams = {}) => {
  try {
    return await getFaxes(apiParams);
  } catch (error) {
    console.error('Error in FaxAgentService.getAll:', error);
    throw new Error('Failed to fetch faxes. Please try again.');
  }
};

/**
 * Creates a new fax
 * 
 * @param {Object} faxData - Fax data to create
 * @param {string} faxData.recipientNumber - Recipient fax number
 * @param {string} faxData.patientName - Patient name
 * @param {string} faxData.patientId - Patient ID
 * @param {string} faxData.subject - Fax subject
 * @param {string} faxData.content - Fax content/message
 * @param {Array} faxData.attachments - Array of file attachments
 * @param {string} faxData.priority - Priority level (normal, high, urgent)
 * @returns {Promise<Object>} Created fax response
 * 
 * @example
 * const result = await FaxAgentService.create(faxData);
 */
export const create = async (faxData) => {
  try {
    return await createFax(faxData);
  } catch (error) {
    console.error('Error in FaxAgentService.create:', error);
    throw new Error('Failed to create fax. Please try again.');
  }
};

/**
 * Updates an existing fax
 * 
 * @param {string} faxId - ID of the fax to update
 * @param {Object} faxData - Updated fax data
 * @returns {Promise<Object>} Update response
 * 
 * @example
 * const result = await FaxAgentService.update('fax-id', updatedData);
 */
export const update = async (faxId, faxData) => {
  try {
    return await updateFax(faxId, faxData);
  } catch (error) {
    console.error('Error in FaxAgentService.update:', error);
    throw new Error('Failed to update fax. Please try again.');
  }
};

/**
 * Deletes a fax
 * 
 * @param {string} faxId - ID of the fax to delete
 * @returns {Promise<Object>} Delete response
 * 
 * @example
 * const result = await FaxAgentService.delete('fax-id');
 */
export const deleteFax = async (faxId) => {
  try {
    return await apiDeleteFax(faxId);
  } catch (error) {
    console.error('Error in FaxAgentService.deleteFax:', error);
    throw new Error('Failed to delete fax. Please try again.');
  }
};

// /**
//  * Sends a fax
//  * 
//  * @param {string} faxId - ID of the fax to send
//  * @returns {Promise<Object>} Send response
//  * 
//  * @example
//  * const result = await FaxAgentService.sendFax('fax-id');
//  */
// export const sendFax = async (faxId) => {
//   try {
//     return await apiSendFax(faxId);
//   } catch (error) {
//     console.error('Error in FaxAgentService.sendFax:', error);
//     throw new Error('Failed to send fax. Please try again.');
//   }
// };

/**
 * Creates a fax template
 * 
 * @param {Object} templateData - Template data
 * @param {string} templateData.name - Template name
 * @param {string} templateData.subject - Template subject
 * @param {string} templateData.content - Template content
 * @param {Array} templateData.attachments - Template attachments
 * @returns {Promise<Object>} Template creation response
 * 
 * @example
 * const result = await FaxAgentService.createTemplate(templateData);
 */
export const createTemplate = async (templateData) => {
  try {
    return await createFaxTemplate(templateData);
  } catch (error) {
    console.error('Error in FaxAgentService.createTemplate:', error);
    throw new Error('Failed to create template. Please try again.');
  }
};

/**
 * Gets fax templates
 * 
 * @returns {Promise<Array>} Array of fax templates
 * 
 * @example
 * const templates = await FaxAgentService.getTemplates();
 */
export const getTemplates = async () => {
  try {
    return await getFaxTemplates();
  } catch (error) {
    console.error('Error in FaxAgentService.getTemplates:', error);
    throw new Error('Failed to fetch templates. Please try again.');
  }
};

/**
 * Gets purchased fax numbers
 * 
 * @returns {Promise<Array>} Array of purchased fax numbers
 * 
 * @example
 * const purchasedNumbers = await FaxAgentService.getPurchasedNumbers();
 */
export const getPurchasedNumbers = async () => {
  try {
    return await getPurchasedFaxNumbers();
  } catch (error) {
    console.error('Error in FaxAgentService.getPurchasedNumbers:', error);
    throw new Error('Failed to fetch purchased numbers. Please try again.');
  }
};

// ============================================================================
// FAX DASHBOARD SERVICE FUNCTIONS - ALL BUSINESS LOGIC HERE
// ============================================================================

/**
 * Fetches fax dashboard data with server-side filtering
 * 
 * @param {Object} params - Query parameters
 * @param {string} [params.direction] - Direction filter (INBOUND, OUTBOUND, DRAFT)
 * @param {number} [params.limit=50] - Number of records to fetch
 * @param {number} [params.skip=0] - Number of records to skip for pagination
 * @returns {Promise<Object>} Dashboard data with pagination info
 */
export const getDashboardData = async (params = {}) => {
  try {
    // Pass all parameters to API client (direction, limit, skip)
    const response = await getFaxDashboard(params);
    
    // Handle different response structures
    let data = [];
    let totalCount = 0;
    
    if (Array.isArray(response)) {
      // If response is directly an array
      data = response;
      totalCount = response.length;
    } else if (response && typeof response === 'object') {
      // If response is an object with data/faxes and totalCount
      data = response.data || response.faxes || response.response || [];
      totalCount = response.totalCount || response.total || 0;
    }
    
    const result = {
      data: data,
      totalCount: totalCount,
      page: Math.floor((params.skip || 0) / (params.limit || 50)),
      limit: params.limit || 50
    };
    
    console.log('📊 Processed service response:', result);
    console.log('📊 Total count being returned:', totalCount);
    console.log('📊 Data length:', data.length);
    return result;
  } catch (error) {
    console.error('❌ Error in FaxAgentService.getDashboardData:', error);
    throw new Error('Failed to fetch dashboard data. Please try again.');
  }
};

/**
 * Fetches inbound fax data (received faxes) with pagination
 * BUSINESS LOGIC: Uses direction=INBOUND for server-side filtering
 * API Call: /fax/fax-dashboard?direction=INBOUND&limit=50&skip=0
 * 
 * @param {Object} params - Pagination parameters
 * @param {number} [params.limit=50] - Number of records to fetch (dynamic)
 * @param {number} [params.skip=0] - Number of records to skip for pagination (dynamic)
 * @returns {Promise<Object>} Inbound fax data with pagination info
 * 
 * @example
 * const inboundData = await FaxAgentService.getInbound({ limit: 25, skip: 0 });
 */
export const getInbound = async (params = {}) => {
  try {
    // Business logic: Pass direction=INBOUND to API for server-side filtering
    const queryParams = {
      direction: 'INBOUND',  // Server-side filter for inbox
      limit: params.limit || 50,  // Dynamic limit
      skip: params.skip || 0       // Dynamic skip
    };
    
    const result = await getDashboardData(queryParams);
    
    return result;
  } catch (error) {
    console.error('❌ Error in FaxAgentService.getInbound:', error);
    throw new Error('Failed to fetch inbound faxes. Please try again.');
  }
};

/**
 * Fetches outbound fax data (sent faxes) with pagination
 * BUSINESS LOGIC: Uses direction=OUTBOUND for server-side filtering
 * API Call: /fax/fax-dashboard?direction=OUTBOUND&limit=50&skip=0
 * 
 * @param {Object} params - Pagination parameters
 * @param {number} [params.limit=50] - Number of records to fetch (dynamic)
 * @param {number} [params.skip=0] - Number of records to skip for pagination (dynamic)
 * @returns {Promise<Object>} Outbound fax data with pagination info
 * 
 * @example
 * const outboundData = await FaxAgentService.getOutbound({ limit: 25, skip: 25 });
 */
export const getOutbound = async (params = {}) => {
  try {
    // Business logic: Pass direction=OUTBOUND to API for server-side filtering
    const queryParams = {
      direction: 'OUTBOUND', // Server-side filter for sent
      limit: params.limit || 50,  // Dynamic limit
      skip: params.skip || 0       // Dynamic skip
    };
    
    const result = await getDashboardData(queryParams);
    
    return result;
  } catch (error) {
    console.error('❌ Error in FaxAgentService.getOutbound:', error);
    throw new Error('Failed to fetch outbound faxes. Please try again.');
  }
};

/**
 * Fetches draft fax data with pagination
 * BUSINESS LOGIC: Uses direction=DRAFT for server-side filtering
 * API Call: /fax/fax-dashboard?direction=DRAFT&limit=50&skip=0
 * 
 * @param {Object} params - Pagination parameters
 * @param {number} [params.limit=50] - Number of records to fetch (dynamic)
 * @param {number} [params.skip=0] - Number of records to skip for pagination (dynamic)
 * @returns {Promise<Object>} Draft fax data with pagination info
 * 
 * @example
 * const draftData = await FaxAgentService.getDrafts({ limit: 25, skip: 0 });
 */
export const getDrafts = async (params = {}) => {
  try {
    // Business logic: Pass direction=DRAFT to API for server-side filtering
    const queryParams = {
      direction: 'DRAFT',    // Server-side filter for drafts
      limit: params.limit || 50,  // Dynamic limit
      skip: params.skip || 0       // Dynamic skip
    };
    
    const result = await getDashboardData(queryParams);
    
    return result;
  } catch (error) {
    console.error('❌ Error in FaxAgentService.getDrafts:', error);
    throw new Error('Failed to fetch draft faxes. Please try again.');
  }
};

/**
 * Gets fax details by ID with business logic processing
 * 
 * @param {string} faxId - The fax ID (_id or faxId from API response)
 * @returns {Promise<Object>} Processed fax details
 * 
 * @example
 * const faxDetails = await FaxAgentService.getFaxDetails('68dcc70a0c2ddf79062c13eb');
 */
export const getFaxDetails = async (faxId) => {
  try {
    // Call raw API to get fax details
    const response = await getFaxById(faxId);
    
    // Process the response with business logic
    const processedFax = {
      ...response,
      // Format display fields
      displayStatus: formatStatus(response.status || response.direction),
      formattedFromNumber: formatFaxNumber(response.fromNumber),
      formattedToNumber: formatFaxNumber(response.toNumber),
      // Process files array
      fileCount: response.files?.length || 0,
      totalPages: response.meta?.totalPages || 0,
      totalCost: response.meta?.totalCost || 0,
      // Extract file URLs for display
      fileUrls: response.files?.map(f => f.fileUrl) || []
    };
    
    return processedFax;
  } catch (error) {
    console.error('❌ Error in FaxAgentService.getFaxDetails:', error);
    throw new Error('Failed to fetch fax details. Please try again.');
  }
};

// ============================================================================
// SEND FAX VALIDATION SCHEMA AND SERVICE
// ============================================================================

/**
 * Yup validation schema for sending fax
 */
export const sendFaxValidationSchema = yup.object().shape({
  toNumber: yup
    .string()
    .required('Recipient fax number is required')
    .matches(/^\+?[1-9]\d{1,14}$/, 'Please enter a valid fax number (e.g., +19725329272)')
    .min(10, 'Fax number must be at least 10 digits')
    .max(15, 'Fax number must not exceed 15 digits'),
    
  fromNumber: yup
    .string()
    .required('Sender fax number is required')
    .matches(/^\+?[1-9]\d{1,14}$/, 'Please enter a valid fax number (e.g., +19073131400)')
    .min(10, 'Fax number must be at least 10 digits')
    .max(15, 'Fax number must not exceed 15 digits'),
    
  // customerId: yup
  //   .string()
  //   .required('Customer ID is required'),
    
  emailId: yup
    .string()
    .required('Email address is required')
    .email('Please enter a valid email address'),
    
  subject: yup
    .string()
    .required('Subject is required')
    .min(3, 'Subject must be at least 3 characters')
    .max(200, 'Subject must not exceed 200 characters'),
    
  fromRefType: yup
    .string()
    .optional()
    .oneOf(['FACILITY', 'PROVIDER', 'USER'], 'Invalid reference type'),
    
  fromRefId: yup
    .string()
    .optional()
    .when('fromRefType', {
      is: (fromRefType) => !!fromRefType,
      then: (schema) => schema.required('Reference ID is required when reference type is provided'),
      otherwise: (schema) => schema
    })
});

/**
 * Validates fax data before submission
 * 
 * @param {Object} faxData - Fax data to validate
 * @returns {Object} Validation result
 * 
 * @example
 * const validation = FaxAgentService.validateData(faxData);
 * if (!validation.isValid) console.log(validation.errors);
 */
export const validateData = (faxData) => {
  const errors = [];
  
  if (!faxData.recipientNumber?.trim()) {
    errors.push('Recipient fax number is required');
  } else if (!/^\+?[\d\s\-\(\)]+$/.test(faxData.recipientNumber)) {
    errors.push('Please enter a valid fax number');
  }
  
  if (!faxData.patientName?.trim()) {
    errors.push('Patient name is required');
  }
  
  if (!faxData.patientId?.trim()) {
    errors.push('Patient ID is required');
  }
  
  if (!faxData.subject?.trim()) {
    errors.push('Subject is required');
  }
  
  if (!faxData.content?.trim()) {
    errors.push('Content is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Formats fax status for display
 * Based on API response structure with direction and status fields
 * 
 * @param {string} statusOrDirection - Status or direction from API response
 * @returns {string} Formatted status
 * 
 * @example
 * const formatted = FaxAgentService.formatStatus('SENT'); // Returns "Sent"
 */
export const formatStatus = (statusOrDirection) => {
  const statusMap = {
    // Status mappings (primary)
    'SENT': 'Sent',
    'RECEIVED': 'Received', 
    'DRAFT': 'Draft',
    'FAILED': 'Failed',
    'PENDING': 'Pending',
    // Direction mappings (fallback)
    'OUTBOUND': 'Sent',
    'INBOUND': 'Received'
  };
  
  return statusMap[statusOrDirection] || statusOrDirection;
};

/**
 * Formats fax priority for display
 * 
 * @param {string} priority - Fax priority
 * @returns {string} Formatted priority
 * 
 * @example
 * const formatted = FaxAgentService.formatPriority('high');
 */
export const formatPriority = (priority) => {
  const priorityMap = {
    'normal': 'Normal',
    'high': 'High',
    'urgent': 'Urgent'
  };
  
  return priorityMap[priority] || priority;
};

/**
 * Formats fax number for display as +1 (XXX) XXX-XXXX
 * 
 * @param {string} number - Fax number
 * @returns {string} Formatted fax number
 * 
 * @example
 * const formatted = FaxAgentService.formatFaxNumber('+15551234567'); // Returns '+1 (555) 123-4567'
 */
export const formatFaxNumber = (number) => {
  if (!number) return '';
  
  // Remove all non-digits
  const digits = number.replace(/\D/g, '');
  
  // Format as +1 (XXX) XXX-XXXX for US numbers
  if (digits.length === 11 && digits[0] === '1') {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  } else if (digits.length === 10) {
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  
  return number;
};

/**
 * Gets template structure for fax templates
 * 
 * @returns {Promise<Object>} Template structure with branches, usecases, and sub-usecases
 * 
 * @example
 * const structure = await FaxAgentService.getTemplateStructure();
 */
export const getTemplateStructure = async () => {
  try {
    const response = await getFaxTemplateStructure();
    return response;
  } catch (error) {
    console.error('❌ Error fetching template structure:', error);
    throw new Error('Failed to fetch template structure. Please try again.');
  }
};

/**
 * Gets signed URL for a template file
 * 
 * @param {Object} templateData - Template data with branch, usecase, subusecase, and filename
 * @param {string} templateData.branch - The selected branch (e.g., "Dental")
 * @param {string} templateData.usecase - The selected usecase (e.g., "Claims")
 * @param {string} templateData.subusecase - The selected subusecase (e.g., "Denials")
 * @param {string} templateData.filename - The template filename (e.g., "template.pdf")
 * @returns {Promise<string>} Signed URL for the template file
 * 
 * @example
 * const signedUrl = await FaxAgentService.getTemplateSignedUrl({
 *   branch: "Dental",
 *   usecase: "Claims", 
 *   subusecase: "Denials",
 *   filename: "template.pdf"
 * });
 */
export const getTemplateSignedUrl = async (templateData) => {
  try {
    const response = await getFaxTemplateSignedUrl({
      branch: templateData.branch,
      usecase: templateData.usecase,
      subusecase: templateData.subusecase,
      filename: templateData.filename,
      expiration_minutes: templateData.expiration_minutes || 60
    });
    
    // Return the full response object so the caller can extract what they need
    // The response structure is: { statusCode: "SUCCESS", response: { signed_url: "...", ... }, message: "..." }
    return response;
  } catch (error) {
    console.error('❌ Error fetching template signed URL:', error);
    throw new Error('Failed to get template signed URL. Please try again.');
  }
};

/**
 * Download template file via backend proxy endpoint (bypasses CORS issues)
 * @param {Object} templateData - Template data with branch, usecase, subusecase, filename
 * @returns {Promise<Blob>} The downloaded template file as a blob
 */
export const downloadTemplateFile = async (templateData) => {
  try {
    return await apiDownloadTemplateFile(templateData);
  } catch (error) {
    console.error('❌ Error downloading template file:', error);
    throw new Error('Failed to download template file. Please try again.');
  }
};

/**
 * Download fax file via backend proxy endpoint (bypasses CORS issues)
 * @param {string} fileUrl - The file URL (GCS path or signed URL)
 * @returns {Promise<Blob>} The downloaded fax file as a blob
 */
export const downloadFaxFile = async (fileUrl) => {
  try {
    return await apiDownloadFaxFile(fileUrl);
  } catch (error) {
    console.error('❌ Error downloading fax file:', error);
    throw new Error('Failed to download fax file. Please try again.');
  }
};

/**
 * Prepares the FormData and sends the fax using the centralized API client function.
 * This is the primary service function for sending a new fax.
 * @param {Object} payload - The complete fax payload from the UI component.
 * @returns {Promise<Object>} The API response.
 */
export const sendFax = async (payload) => {
  try {
    const formData = new FormData();
    
    // Required fields
    formData.append('toNumber', payload.toNumber);
    formData.append('fromNumber', payload.fromNumber);
    formData.append('emailId', payload.emailId);
    if (payload.coverpageFile) formData.append('coverpageFile', payload.coverpageFile);
    
    // Optional fields - only append if they have a value
    if (payload.templateFile) {
      formData.append('templateFile', payload.templateFile);
    }
    if (payload.subject) {
      formData.append('subject', payload.subject);
    }
    if (payload.fromRefType) {
      formData.append('fromRefType', payload.fromRefType);
    }
    if (payload.fromRefId) {
      formData.append('fromRefId', payload.fromRefId);
    }
    if (payload.description) {
      formData.append('description', payload.description);
    }
    if (payload.patientName) {
      formData.append('patientName', payload.patientName);
    }
    
    // Medical records (multiple files)
    if (payload.medicalRecordsFiles && payload.medicalRecordsFiles.length > 0) {
      payload.medicalRecordsFiles.forEach((file) => {
        formData.append('medicalRecordsFiles', file);
      });
    }
    
    // Call the centralized API client function with the prepared FormData
    const result = await apiSendFax(formData);
    
    return result;
    
  } catch (error) {
    console.error('❌ Error in FaxAgentService.sendFax:', error);
    throw new Error(error.message || 'Failed to send fax. Please try again.');
  }
};

/**
 * Sends a fax with validation and file handling.
 * This function validates the payload, prepares FormData, and sends the fax.
 * Used by FaxAgentDashboard for sending fax with separate file array.
 * @param {Object} faxData - The fax metadata (toNumber, fromNumber, emailId, subject, etc.)
 * @param {File[]} files - Array of files to attach (first file is treated as cover page)
 * @returns {Promise<Object>} The API response.
 */
export const sendFaxWithValidation = async (faxData, files = []) => {
  try {
    // Validate required fields using yup schema
    await sendFaxValidationSchema.validate(faxData, { abortEarly: false });
    
    const formData = new FormData();
    
    // Required fields
    formData.append('toNumber', faxData.toNumber);
    formData.append('fromNumber', faxData.fromNumber);
    formData.append('emailId', faxData.emailId);
    
    // Optional fields - only append if they have a value
    if (faxData.subject) {
      formData.append('subject', faxData.subject);
    }
    if (faxData.fromRefType) {
      formData.append('fromRefType', faxData.fromRefType);
    }
    if (faxData.fromRefId) {
      formData.append('fromRefId', faxData.fromRefId);
    }
    if (faxData.description) {
      formData.append('description', faxData.description);
    }
    
    // Handle files - first file is cover page, rest are medical records
    if (files && files.length > 0) {
      // First file is the cover page
      formData.append('coverpageFile', files[0]);
      
      // Remaining files are medical records
      if (files.length > 1) {
        for (let i = 1; i < files.length; i++) {
          formData.append('medicalRecordsFiles', files[i]);
        }
      }
    }
    
    // Call the centralized API client function with the prepared FormData
    const result = await apiSendFax(formData);
    
    return result;
    
  } catch (error) {
    // Handle Yup validation errors
    if (error.name === 'ValidationError') {
      const validationErrors = error.inner.map(err => err.message).join(', ');
      throw new Error(`Validation failed: ${validationErrors}`);
    }
    console.error('❌ Error in FaxAgentService.sendFaxWithValidation:', error);
    throw new Error(error.message || 'Failed to send fax. Please try again.');
  }
};

// Export as default object for easier importing
const FaxAgentService = {
  getAll,
  create,
  update,
  deleteFax,
  sendFax,
  sendFaxWithValidation,
  createTemplate,
  getTemplates,
  getPurchasedNumbers,
  getDashboardData,
  getInbound,
  getOutbound,
  getDrafts,
  getFaxDetails,
  sendFaxValidationSchema,
  validateData,
  formatStatus,
  formatPriority,
  formatFaxNumber,
  getTemplateStructure,
  getTemplateSignedUrl,
  downloadTemplateFile,
  downloadFaxFile
};

export default FaxAgentService;